import * as React from 'react';

import {
  colorToHSL,
  isValidColor,
  hslToString,
  HSLA,
} from '@packages/systems/core/utils/ColorUtils';
import {Plugins, TypeImplementation, Result} from '@packages/systems/wfdl';
import {call1, _$, $, func, tyfun} from '@packages/utilities/wfdl-helpers';
import {
  ExpressionEditor,
  updateExpressionAction,
} from '@packages/systems/ExpressionEditor-utils';
import * as wast from '@packages/systems/wfdl-ast';
import * as wq from '@packages/systems/wfdl-Qualified';
import {FieldColorSmallIcon as ColorIcon} from '@packages/systems/spring';
import {TOption} from '@packages/systems/plugins/Option';
import {
  TPredicate,
  toBooleanTag,
} from '@packages/systems/plugins-utils/Boolean';
import {ColorPickerInput} from './ColorPickerInput';
import {
  type ContextualExpression,
  ContextualExpressions,
  stripAnn,
} from '@packages/utilities/contextual-expression';
import {colorParseValue} from '@packages/systems/plugins-utils/Color';

export {ColorPickerInput};

export const TColor = wast.TTypeConstructor(
  wq.qualify(Plugins.name('Color'), wq.typeName('Color'))
);

export const TOptionalColor = wast.TTypeConstructor(
  wq.qualify(Plugins.name('Color'), wq.typeName('Optional Color'))
);

const colorResultToString = Result.match<string, HSLA, string>({
  Error: (_) => '',
  Ok: (hsla) => hslToString(hsla) || '',
});

const getLiteral = (expr: ContextualExpression) => {
  const literal = ContextualExpressions.getLiteral(stripAnn(expr));
  if (!literal) return '';
  const res = colorParseValue(literal.value);
  return colorResultToString(res);
};

const makeLiteral = (val: string) =>
  wast.ELiteral({
    name: wq.qualify(Plugins.name('Color'), wq.typeName('Color')),

    value: colorToHSL(val),
  });

function makeRender<ActionType>(
  getValue: (expr: ContextualExpression) => string,
  getAction: (val: string) => ActionType | null
): React.ComponentType<{
  expr: ContextualExpression;
  dispatch: (action: ActionType) => void;
}> {
  return function Color({expr, dispatch}) {
    const dispatchRef = React.useRef(dispatch);
    dispatchRef.current = dispatch;
    const val = getValue(expr);

    // @ts-expect-error - TS7006 - Parameter 'nextValue' implicitly has an 'any' type.
    const onChange = React.useCallback((nextValue) => {
      const action = getAction(nextValue);
      if (action) dispatchRef.current(action);
    }, []);

    return (
      <ColorPickerInput value={val} onChange={onChange} dispatch={dispatch} />
    );
  };
}

const OptionalColor = makeRender(
  (expr: ContextualExpression) => {
    const union = ContextualExpressions.getUnion(stripAnn(expr));
    return union && union.tag === 'Some' ? getLiteral(union.value) : '';
  },
  (newVal) => {
    if (newVal === '')
      return updateExpressionAction(
        wast.EUnion({
          tag: 'None',
          value: wast.ERecord({}),
        })
      );

    if (isValidColor(newVal))
      return updateExpressionAction(
        wast.EUnion({
          tag: 'Some',
          value: makeLiteral(newVal),
        })
      );

    return null;
  }
);

export const plugin = Plugins.PrivilegedPlugin({
  dataTypes: {
    Color: TypeImplementation.Custom({
      parseValue: colorParseValue,
      render: makeRender(getLiteral, (newVal) => {
        if (newVal === '') return updateExpressionAction(wast.EHole());
        return isValidColor(newVal)
          ? updateExpressionAction(makeLiteral(newVal))
          : null;
      }),
      icon: TypeImplementation.IconByName(
        wq.qualify(wq.pluginName('Color'), wq.iconName('Color'))
      ),
    }),
    'Optional Color': TypeImplementation.Alias({
      type: TOption(TColor),
      render: ({expr, dispatch}) =>
        ContextualExpressions.isBinding(stripAnn(expr)) ? (
          <ExpressionEditor expression={expr} dispatch={dispatch} />
        ) : (
          <OptionalColor expr={expr} dispatch={dispatch} />
        ),
    }),
  },

  values: {
    Equals: {
      envType: {
        exported: 'visible',
        dataType: wast.functionOf(TColor, TPredicate(TColor)),
      },
      // @ts-expect-error - TS7006 - Parameter 'color1' implicitly has an 'any' type.
      value: (color1) => (color2: any) =>
        toBooleanTag(
          color1.h === color2.h &&
            color1.s === color2.s &&
            color1.l === color2.l &&
            color1.a === color2.a
        ),
    },

    'Does not equal': {
      envType: {
        exported: 'visible',
        dataType: wast.functionOf(TColor, TPredicate(TColor)),
      },
      // @ts-expect-error - TS7006 - Parameter 'color1' implicitly has an 'any' type.
      value: (color1) => (color2: any) =>
        toBooleanTag(
          color1.h !== color2.h ||
            color1.s !== color2.s ||
            color1.l !== color2.l ||
            color1.a !== color2.a
        ),
    },
    'Parse Color': {
      envType: {
        dataType: wast.functionOf(
          wast.TText,
          wast.TUnion({
            Error: wast.TText,
            Ok: TColor,
          })
        ),
        exported: 'hidden',
      },
      // @ts-expect-error - TS7006 - Parameter 'inputColor' implicitly has an 'any' type.
      value: (inputColor) =>
        isValidColor(inputColor)
          ? {tag: 'Ok', val: colorToHSL(inputColor)}
          : {
              tag: 'Error',
              val: 'Color values can only by hex, rgb, rgba, hsl, or hsla.',
            },
    },
    'To Text': {
      envType: {
        dataType: wast.functionOf(TColor, wast.TText),
        exported: 'visible',
        implicit: 'prefilledArgs',
      },
      value: hslToString,
    },

    'Option Color to Text': {
      envType: {
        dataType: tyfun([wast.TText, TOptionalColor], wast.TText),
        implicit: 'prefilledArgs',
        exported: 'visible',
      },
      expression: func(
        [wq.identifier('fallback text'), wq.identifier('optional color')],
        wast.ECase({
          value: $('optional color'),
          cases: {
            Some: _$('Color', 'To Text'),
            None: func([wq.identifier('_')], $('fallback text')),
          },
        })
      ),
    },
  },
  icons: {
    Color: ColorIcon,
  },
});

export const parseColorRef = _$('Color', 'Parse Color');
export const parseColor = call1(parseColorRef);
